/**
 * The page head's scope (Figma 09 · Page head): who or what the page is about,
 * and the "Change" that switches it. Workspace pages switch through the nav's
 * workspace picker, brand pages through the nav's brand dropdown.
 */
import type { ReactNode } from "react";
import { Store } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGetBrands, type Brand } from "@/hooks/useGetBrands";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { BrandMark } from "../../kit/BrandPicker";
import { EmptyState } from "../../ViewStates";
import { BrandSwitcher } from "./BrandSwitcher";
import { PersonMark, ScopeTile } from "./SettingsParts";

const OPEN_ORG_PICKER = "co:open-org-picker";
const OPEN_SETTINGS_BRAND_PICKER = "co:open-settings-brand-picker";

/** Opens the Settings nav's brand dropdown (not the rail's brand picker). */
export function openSettingsBrandPicker() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_BRAND_PICKER));
}

export function onOpenSettingsBrandPicker(handler: () => void): () => void {
  window.addEventListener(OPEN_SETTINGS_BRAND_PICKER, handler);
  return () => window.removeEventListener(OPEN_SETTINGS_BRAND_PICKER, handler);
}

export function openOrgPicker() {
  window.dispatchEvent(new Event(OPEN_ORG_PICKER));
}

export function onOpenOrgPicker(handler: () => void): () => void {
  window.addEventListener(OPEN_ORG_PICKER, handler);
  return () => window.removeEventListener(OPEN_ORG_PICKER, handler);
}

export type ScopeHead = { mark: ReactNode; scopeName?: string | null; onChange?: () => void };

export function useYouHead(): ScopeHead {
  const { user } = useCurrentUser();
  return { mark: <PersonMark name={user?.name || user?.email} picture={user?.profilePicture} size={36} /> };
}

export function useWorkspaceHead(): ScopeHead & { orgName: string } {
  const { selectedOrganization, user } = useCurrentUser();
  const isDesktop = !useMediaQuery("(max-width: 1023px)");
  const orgName = selectedOrganization?.organization.name ?? "Workspace";
  const many = (user?.userOrganizations?.length ?? 0) > 1;
  return {
    orgName,
    mark: <ScopeTile name={orgName} />,
    scopeName: orgName,
    // The workspace picker lives in the desktop nav.
    onChange: many && isDesktop ? openOrgPicker : undefined,
  };
}

export function useSelectedBrand(): { brand: Brand | null; brands: Brand[]; isLoading: boolean } {
  const selectedBrandId = useWorkspaceStore((s) => s.selectedBrandId);
  const { data, isLoading } = useGetBrands();
  const brands = data ?? [];
  return { brand: brands.find((b) => b.id === selectedBrandId) ?? null, brands, isLoading };
}

export function useBrandHead(): ScopeHead & { brand: Brand | null; brandName: string } {
  const { brand } = useSelectedBrand();
  // The brand dropdown lives in the desktop nav.
  const isDesktop = !useMediaQuery("(max-width: 1023px)");
  const brandName = brand?.name ?? "Brand";
  return {
    brand,
    brandName,
    mark: brand ? <BrandMark brand={brand} size={36} /> : <ScopeTile name={brandName} />,
    scopeName: brand?.name ?? null,
    onChange: isDesktop ? openSettingsBrandPicker : undefined,
  };
}

/** A brand page with no brand open. */
export function NeedBrand() {
  return (
    <EmptyState
      icon={<Store aria-hidden />}
      title="Choose a brand"
      body="These settings belong to one brand. Pick the brand to open its settings."
      actions={<div className="w-[240px]"><BrandSwitcher /></div>}
    />
  );
}
